/**
 * /industries/retail data — rebuilt from
 * src/Design/industries/industry-retail.html, word for word, EXCEPT the
 * four solution features: the design file's own top comment says "The four
 * solution features were drafted from the challenges. Replace them with the
 * included list on the multi-store retail offer page" — done below, from
 * src/data/offers.js's odoo-for-multi-store-retail.included (POS deployment
 * incl. offline-tolerant operation, unified multi-location inventory,
 * eCommerce/marketplace sync, store/region/product reporting + faster
 * month-end close). FAQ checked against the same offer entry; its own faqs
 * array is empty, so there's nothing there to contradict — nothing in the
 * design's FAQ conflicts with `included`/`whoFor`/`symptoms`.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const retail = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "retail",
  industryName: "Retail",

  kicker: "Solutions for retailers selling in stores and online",
  headline: "The same stock count in every store and online",
  subheading:
    "Stores and web shop on one system, so stock, sales and margins agree — from multi-store rollouts to online commerce and payments.",
  heroImage,
  heroImageAlt: "Retail work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Borjan · 25+ supply chain and finance reports",
  stats: [
    { value: "25+", label: "Reports built for Borjan" },
    { value: "15+", label: "Odoo implementations" },
    { value: "35+", label: "Solutions delivered" },
    { value: "30 days", label: "Post-launch warranty" },
  ],

  challengesEyebrow: "What slows retail teams down",
  challengesHeading: "Four ways stock and sales drift apart",
  challengesIntro: "The more stores you open, the wider the gap gets.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Stock that doesn't match",
      detail: "What the till says and what's on the shelf are two different numbers.",
    },
    {
      image: challengeImages[1],
      heading: "Every new store starts over",
      detail: "Opening a location means rebuilding reports and setup again.",
    },
    {
      image: challengeImages[2],
      heading: "Online and in-store apart",
      detail: "Web and store orders live in separate systems, reconciled by hand.",
    },
    {
      image: challengeImages[3],
      heading: "Profit shows at month-end",
      detail: "You can't see which store or product earns until the books close.",
    },
  ],

  situationsEyebrow: "How we help retail teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro:
    "Retailers come to us at very different stages. These are the four we see most, and how we handle each.",
  situations: [
    {
      tag: "Several stores",
      title: "Stock that matches in every store",
      detail: "We put every store and the warehouse on one Odoo stock and sales system, so the counts agree.",
      ghostIconPath:
        '<path d="M4 9l1.5-5h13L20 9"/><path d="M4 9h16v2a3 3 0 0 1-5.3 2 3 3 0 0 1-5.4 0A3 3 0 0 1 4 11z"/><path d="M5 14v6h14v-6"/>',
      footerText: "Multi-store retail",
      footerHref: "/offers/odoo-for-multi-store-retail",
    },
    {
      tag: "Selling online too",
      title: "Online and in-store as one",
      detail: "Your web shop, marketplaces and payments run from the same stock as your stores.",
      ghostIconPath: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>',
      footerText: "Commerce and payments",
      footerHref: "/offers/odoo-for-commerce-and-payments",
    },
    {
      tag: "Need a new till system",
      title: "A point of sale built for retail",
      detail: "VisioEdge, our own point-of-sale system for retail and restaurants, ready to run your tills.",
      ghostIconPath: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 7h6M9 12h1M14 12h1M9 16h1M14 16h1"/>',
      footerText: "VisioEdge",
      footerHref: "/caseStudies/products/visioedge",
    },
    {
      tag: "Already on Dynamics 365",
      title: "Keep the system you have",
      detail: "A named engineer who knows your setup maintains and extends it, instead of replacing it.",
      ghostIconPath: '<path d="M20 11a8 8 0 0 0-14.8-3.5M4 4v4h4"/><path d="M4 13a8 8 0 0 0 14.8 3.5M20 20v-4h-4"/>',
      footerText: "Dynamics 365 support",
      footerHref: "/offers/dynamics-365-support",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for retail clients",
  resultsHeading: "Stock that agrees across every channel",
  resultsMetrics: [
    { value: "Minutes", label: "to run Borjan's supply chain and finance reports, which used to take a week to prepare" },
    { value: "In sync", label: "orders, stock and prices between Dynamics 365 and Xstak for Hush Puppies" },
  ],
  resultsSummary: "Stock stays aligned across stores and online automatically, instead of being matched by hand.",
  resultsLinks: [
    { label: "Borjan", href: "/caseStudies/projects/borjan" },
    { label: "Hush Puppies", href: "/caseStudies/projects/hushPuppiesPakistan" },
  ],
  resultsImage,
  resultsImageAlt: "Borjan",

  // Names resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Borjan", "Hush Puppies", "Huge Sports", "Aamaal Group", "United Al-Zahra"],

  whyEyebrow: "Why Visiomate for retail",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "Built for multi-store rollouts", detail: "We've already built reporting and integrations for one of Pakistan's largest footwear retailers." },
    {
      title: "We know retail on Dynamics 365",
      detail: "Reports, point of sale changes and integrations for Borjan and Hush Puppies.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "Can our stores and online shop run on one system?",
      a: "Yes. Stores, warehouse and web shop share one stock and sales record, so counts match everywhere.",
    },
    {
      q: "We already run Dynamics 365 — do we have to replace it?",
      a: "No. We can maintain and extend the system you have, and only suggest a move if it's genuinely holding you back.",
    },
    {
      q: "Can we start with a few stores and add the rest later?",
      a: "Yes. New locations are added from the same setup, so later stores go live faster than the first.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Retail Software Development Company | Visiomate",
  metaDescription:
    "Fixed-scope Odoo work for retail chains and D2C brands: unified POS and stock, storefront, payments and accounting reconciled automatically.",
})

export default retail
